import { ArrowRight, Building2, LineChart, MessageSquareText, RadioTower, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { AgentPortalHeroDiscoveryTiles } from "@/components/agent/AgentPortalHeroDiscoveryTiles";
import { HomepageTypewriterHeadline } from "@/components/landing/HomepageTypewriterHeadline";

const ROTATING_AUDIENCE_PHRASES = ["Buyers", "Renters", "Sellers", "Landlords"];

const CONSUMER_PATHS = [
  { key: "buy", label: "Buy", body: "Search live listings, compare districts, and shortlist homes faster.", href: "/buy" },
  { key: "rent", label: "Rent", body: "Find ready-to-move rental options with one cleaner response path.", href: "/rent" },
  { key: "sell", label: "Sell", body: "Request valuation guidance and seller positioning support before going live.", href: "/sell" },
  { key: "lease-out", label: "Lease Out", body: "Plan rental pricing and get landlord support before listing your unit.", href: "/lease-out" },
];

const AGENT_PROOF_ITEMS = [
  { key: "portal", icon: Building2, label: "Own branded portal" },
  { key: "advisor", icon: MessageSquareText, label: "AI chatbot + advisor" },
  { key: "analytics", icon: LineChart, label: "Real-time analytics" },
  { key: "routing", icon: RadioTower, label: "Automated follow-ups" },
];

export const DualAudienceHero = ({ buildBrandedPath, foundingRibbon, getWhatsAppHref, inventorySummary, isAgentPortal = false, openLivePortal }) => {
  return (
    <section className="section-shell pt-8" data-testid="dual-audience-hero">
      <div className="grid gap-6 lg:grid-cols-2 lg:items-stretch">
        <div className="rounded-[32px] border border-[#001935]/10 bg-[linear-gradient(180deg,rgba(0,25,53,0.98),rgba(4,33,67,0.94))] p-8 shadow-[0_28px_80px_rgba(0,25,53,0.2)] lg:p-10" data-testid="dual-audience-consumer-panel">
          <p className="!text-[#b3935d] text-xs font-bold uppercase tracking-[0.24em]">For Buyers · Sellers · Landlords · Tenants</p>
          <HomepageTypewriterHeadline
            className="mt-4 text-4xl font-semibold leading-tight text-white md:text-6xl"
            phrases={ROTATING_AUDIENCE_PHRASES}
            prefix="Find, buy, rent, sell, or lease out with clarity for"
            prefixClassName="text-white"
            rotatingClassName="inline-block min-h-[1.15em] align-baseline text-[#b3935d]"
            testIdPrefix="dual-audience-consumer-heading"
          />
          <p className="mt-4 max-w-2xl !text-[#F3E8D3] text-sm leading-8 md:text-base" data-testid="dual-audience-consumer-copy">
            Search live listings, explore districts, request valuations, and connect directly with the right advisor faster.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="dual-audience-consumer-path-grid">
            {CONSUMER_PATHS.map((item) => (
              <Link
                className="rounded-[24px] border border-white/10 bg-white/6 p-5 transition duration-300 hover:-translate-y-0.5 hover:border-[#b3935d]/36 hover:bg-white/8 hover:shadow-[0_16px_40px_rgba(0,25,53,0.16)]"
                data-testid={`consumer-router-${item.key}-card`}
                key={item.key}
                to={buildBrandedPath(item.href)}
              >
                <p className="!text-[#b3935d] text-[11px] font-bold uppercase tracking-[0.18em]">{item.label}</p>
                <p className="mt-3 !text-white text-lg font-semibold">{item.body}</p>
                <span className="mt-4 inline-flex items-center gap-2 !text-[#F3E8D3] text-sm font-semibold">
                  Start here <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link className="gold-button" data-testid="dual-audience-consumer-primary-cta" to={buildBrandedPath("/buy")}>
              Browse Homes <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <Link className="outline-button !border-white/22 !text-white hover:!bg-white/8" data-testid="dual-audience-consumer-contact-cta" to={buildBrandedPath("/contact")}>
              Contact Us
            </Link>
          </div>

          {isAgentPortal ? <AgentPortalHeroDiscoveryTiles buildBrandedPath={buildBrandedPath} surface="dark" testIdPrefix="agent-portal-classic-hero-discovery" /> : null}

          <div className="mt-8 grid gap-3 sm:grid-cols-3" data-testid="dual-audience-consumer-proof-grid">
            {[
              { label: "Unique homes", value: inventorySummary.cleanTotal || 139 },
              { label: "Districts covered", value: "28" },
              { label: "Response mode", value: "WhatsApp-first" },
            ].map((item) => (
              <div className="rounded-[22px] border border-white/10 bg-white/6 px-4 py-4" data-testid={`dual-audience-consumer-proof-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={item.label}>
                <p className="!text-[#b3935d] text-[11px] font-bold uppercase tracking-[0.16em]">{item.label}</p>
                <p className="mt-3 !text-white text-xl font-semibold">{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[32px] border border-[#D7C39A]/45 bg-[linear-gradient(180deg,#FFFDF7_0%,#F8F1E3_100%)] p-8 text-[#0C285A] shadow-[0_28px_90px_rgba(12,40,90,0.08)] lg:p-10" data-testid="dual-audience-agent-panel">
          <p className="!text-[#B8860B] text-xs font-bold uppercase tracking-[0.24em]">For Agents</p>
          <div className="mt-4 inline-flex items-center rounded-full border border-[#D7C39A]/60 bg-[#FFF4DB] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#B8860B]" data-testid="dual-audience-agent-badge">
            Live Sample Portal + Pro Plan
          </div>
          <h2 className="mt-4 text-4xl font-semibold leading-tight text-[#0C285A] md:text-[3.2rem]" data-testid="dual-audience-agent-heading">
            Build your own branded portal and close more deals with Pro Plan.
          </h2>
          <p className="mt-4 max-w-2xl !text-[#42566f] text-sm leading-8 md:text-base" data-testid="dual-audience-agent-copy">
            Own your leads, automate follow-up, and give clients a premium property experience without relying only on shared portals.
          </p>

          <div className="mt-8 grid gap-3 sm:grid-cols-2" data-testid="dual-audience-agent-proof-grid">
            {AGENT_PROOF_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <div className="rounded-[22px] border border-[#D7C39A]/36 bg-white/72 px-4 py-4" data-testid={`dual-audience-agent-proof-${item.key}`} key={item.key}>
                  <div className="flex items-center gap-3">
                    <div className="rounded-full border border-[#B8860B]/22 bg-[#FFF4DB] p-2.5 text-[#B8860B]">
                      <Icon className="h-4 w-4" />
                    </div>
                    <p className="!text-[#0C285A] text-sm font-semibold">{item.label}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <button className="gold-button" data-testid="dual-audience-agent-demo-button" onClick={openLivePortal} type="button">
              View Live Sample Portal <ArrowRight className="ml-2 h-4 w-4" />
            </button>
            <Link className="outline-button !border-[#0C285A]/16 !text-[#0C285A] hover:!bg-[#0C285A]/5" data-testid="dual-audience-agent-learn-button" to="/for-agents">
              Explore For Agents
            </Link>
            <Link className="outline-button !border-[#0C285A]/16 !text-[#0C285A] hover:!bg-[#0C285A]/5" data-testid="dual-audience-agent-pricing-button" to="/pricing">
              Review Pro Plan
            </Link>
            <Link className="gold-button" data-testid="dual-audience-agent-signup-button" to="/signup?plan=pro">
              Launch Pro Plan <Sparkles className="ml-2 h-4 w-4" />
            </Link>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-1" data-testid="dual-audience-agent-stats-grid">
            {[
              { label: "Live proof", value: "Clive portal" },
            ].map((item) => (
              <div className="rounded-[22px] border border-[#D7C39A]/36 bg-white/72 px-4 py-4" data-testid={`dual-audience-agent-stat-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={item.label}>
                <p className="!text-[#B8860B] text-[11px] font-bold uppercase tracking-[0.16em]">{item.label}</p>
                <p className="mt-3 !text-[#0C285A] text-xl font-semibold">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};